/**
 * Hook for fetching and managing accounts data.
 * Uses SWR for caching and automatic revalidation.
 */

import useSWR from 'swr';
import { fetchAccounts, fetchAccountSummary } from '@/services/api';
import type { Account, AccountSummary } from '@/types';

/**
 * Fetch all available accounts.
 */
export function useAccounts() {
  const { data, error, isLoading, mutate } = useSWR<Account[]>(
    'accounts',
    fetchAccounts,
    {
      revalidateOnFocus: false,
      dedupingInterval: 60000, // Cache for 60 seconds
    }
  );

  return {
    accounts: data ?? [],
    error: error?.message ?? null,
    isLoading,
    refresh: mutate,
  };
}

/**
 * Fetch account summary for a specific account.
 */
export function useAccountSummary(accountId: string | null) {
  const { data, error, isLoading, mutate } = useSWR<AccountSummary | null>(
    accountId ? ['account-summary', accountId] : null,
    () => (accountId ? fetchAccountSummary(accountId) : null),
    {
      refreshInterval: 60000, // Refresh every 60 seconds
      revalidateOnFocus: true,
    }
  );

  return {
    summary: data ?? null,
    error: error?.message ?? null,
    isLoading,
    refresh: mutate,
  };
}
